/**
 * @module Editor options for the Tiptap prompt editor.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), carrying Forge's editor
 * element contract: typography tokens, surface geometry, test hooks.
 */

import { type Editor, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TaskList } from "@tiptap/extension-task-list";

import { collectComposerPromptInlineTokens } from "~/composer-editor-mentions";
import {
  buildDocJson,
  ComposerCodeExtension,
  ComposerTaskItemExtension,
  insertMarkdownParagraphs,
  type SkillMeta,
} from "~/composer-rich-text-doc";
import type { TerminalContextDraft } from "~/lib/terminalContext";
import { cn } from "~/lib/utils";
import {
  ComposerMentionExtension,
  ComposerSkillExtension,
  ComposerTerminalContextExtension,
} from "./composerPromptEditorChips";
import { ComposerCitationExtension } from "./ComposerCitationNode";
import { ComposerMarkersExtension } from "./composerPromptEditorMarkers";
import { handleComposerKeyDown, handleComposerTextInput } from "./composerPromptEditorKeys";

export interface ComposerEditorConfigInput {
  /** Live editor accessor for the key and input handlers. */
  readonly getEditor: () => Editor | null;
  /** Doc and selection change pump (the same callback for both events). */
  readonly onEditorChange: (editor: Editor) => void;
  readonly richText: boolean;
  readonly value: string;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly skillLabelFor: (name: string) => SkillMeta;
  readonly disabled: boolean;
  readonly placeholder: string;
  readonly className?: string;
  readonly onCommandKeyDown?: (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
    editorValue: string,
  ) => boolean;
  /** Notified when a Backspace deletes a terminal-context chip. */
  readonly onRemoveTerminalContext: (contextId: string) => void;
}

/** Options object for `useEditor`, rebuilt only when the engine half flips. */
export function buildComposerEditorOptions(
  input: ComposerEditorConfigInput,
): Parameters<typeof useEditor>[0] {
  const { richText, value, terminalContexts, skillLabelFor, disabled, placeholder, className } =
    input;
  return {
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        listItem: false,
        link: false,
        orderedList: false,
        underline: false,
        dropcursor: false,
        gapcursor: false,
        trailingNode: false,
        code: false,
        // Plain mode has no marks: typed markers stay literal characters.
        ...(richText ? {} : { bold: false, italic: false, strike: false }),
      }),
      ComposerMentionExtension,
      ComposerSkillExtension,
      ComposerTerminalContextExtension,
      ComposerCitationExtension,
      ComposerMarkersExtension,
      ...(richText ? [ComposerCodeExtension, TaskList, ComposerTaskItemExtension] : []),
    ],
    content: buildDocJson(value, skillLabelFor, { styling: richText, terminalContexts }),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          "composer-tiptap block max-h-[200px] min-h-17.5 w-full cursor-text overflow-y-auto whitespace-pre-wrap break-words bg-transparent text-[length:var(--forge-prompt-font-size,14px)] font-[family-name:var(--font-composer,var(--font-sans))] leading-relaxed text-foreground focus:outline-none",
          className,
        ),
        "data-testid": "composer-editor",
        "data-composer-rich-text": richText ? "true" : "false",
        "aria-placeholder": placeholder,
      },
      handleKeyDown: (view, event) =>
        handleComposerKeyDown(view, event, {
          onCommandKeyDown: input.onCommandKeyDown,
          onRemoveTerminalContext: input.onRemoveTerminalContext,
          richText,
          isActive: (name) => input.getEditor()?.isActive(name) ?? false,
          splitTaskItem: () =>
            input.getEditor()?.commands.splitListItem("taskItem", { checked: false }) ?? false,
          liftEmptyTaskItem: () => input.getEditor()?.commands.liftListItem("taskItem") ?? false,
        }),
      handleTextInput: handleComposerTextInput,
      // fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData || clipboardData.files.length > 0) return false;
        const pastedText = clipboardData.getData("text/plain");
        if (!pastedText) return false;
        event.preventDefault();
        let text = pastedText;
        // Complete chips at paste boundaries just as autocomplete does.
        const tokens = collectComposerPromptInlineTokens(`${text}\n`);
        const lastToken = tokens.at(-1);
        if (
          (lastToken?.type === "mention" || lastToken?.type === "skill") &&
          lastToken.end === text.length
        ) {
          text += " ";
        }
        const instance = input.getEditor();
        if (instance) {
          insertMarkdownParagraphs(
            text,
            skillLabelFor,
            { styling: richText, terminalContexts },
            (content) => {
              instance.commands.insertContent(content as never);
            },
          );
          instance.view.dispatch(instance.state.tr.scrollIntoView());
        }
        return true;
      },
    },
    onUpdate: ({ editor }) => {
      input.onEditorChange(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      input.onEditorChange(editor);
    },
  };
}
